class HeaderDetailsDisclosure extends DetailsDisclosure {
  constructor() {
    super();

    this._boundMouseEnterListener = this.mouseEnterListener.bind(this);
    this._boundMouseLeaveListener = this.mouseLeaveListener.bind(this);
  }

  connectedCallback() {
    this._summaryLink = this.querySelector('summary > a');

    if (!this.mainDetailsToggle.hasAttribute('open'))
      this._disableSummaryLink();

    // Mega menu triggers (e.g. Shop, Made-to-Measure) only open their panel, never navigate
    if (this.hasAttribute('summary-link-disabled') && this._summaryLink) {
      this._summaryLink.addEventListener('click', (e) => {
        e.preventDefault();
        if (!this.mainDetailsToggle.hasAttribute('open')) this.open();
      });
    }

    // Inside <nested-menu> (desktop top menu) hover is handled by nested-menu.js, which calls
    // mouseEnterListener / mouseLeaveListener itself. Adding our own listeners as well made the two
    // get out of sync: after the first close, hovering never reopened the menu (only a click did).
    if (
      this.closest('nested-menu') &&
      window.matchMedia &&
      window.matchMedia('(any-hover: hover)').matches
    )
      return;

    this.mainDetailsToggle.addEventListener(
      'mouseenter',
      this._boundMouseEnterListener,
    );

    this.mainDetailsToggle.addEventListener(
      'mouseleave',
      this._boundMouseLeaveListener,
    );
  }

  disconnectedCallback() {
    this.disableListeners();
  }

  open() {
    // Moving straight from one mega menu to another: swap the panels without the fade-in,
    // so the page underneath doesn't flash through between them
    if (this.classList.contains('mega')) {
      const anotherOpen = !!document.querySelector(
        'header-details-disclosure.mega details[open]',
      );
      const justClosed =
        performance.now() - (HeaderDetailsDisclosure._lastMegaClose || -1e9) <
        150;
      this.content.classList.toggle('is-switching', anotherOpen || justClosed);
    }

    this.mainDetailsToggle.setAttribute('open', '');
    this.querySelector('summary').setAttribute('aria-expanded', true);

    if (
      this.content.getBoundingClientRect().x + this.content.offsetWidth >
      window.innerWidth - 30
    ) {
      this.content.classList.add('is-left-aligned');
    }

    if (this.hasAttribute('adjust-mega-menu-height'))
      this._setContentHeight(36);

    if (this.classList.contains('mega')) {
      this._setHoverBridge();
      HeaderDetailsDisclosure._setColumnHoverAreas(this);
    }
  }

  // The open panel's bridge spans the whole width, so it also covered the empty band under the
  // other top links: moving sideways through that band kept the current menu open and the next one
  // never opened. Give every mega trigger a hover area over the band under its own column
  // (drawn above the open bridge in header-nav-redesign-styles.liquid).
  // All mega panels open at the same height (under the header), so measure the one being opened:
  // closed panels don't report their real position.
  static _setColumnHoverAreas(openMenu) {
    const transform = getComputedStyle(openMenu.content).transform;
    const shiftY =
      transform && transform !== 'none' ? new DOMMatrixReadOnly(transform).m42 : 0;
    const panelTop = openMenu.content.getBoundingClientRect().top - shiftY;

    document
      .querySelectorAll('header-details-disclosure.mega')
      .forEach((menu) => {
        const summary = menu.querySelector('summary');
        if (!summary) return;
        const gap = Math.max(
          0,
          Math.round(panelTop - summary.getBoundingClientRect().bottom),
        );
        summary.style.setProperty('--hover-column-height', `${gap + 2}px`);
      });
  }

  // Invisible strip between the top link and the mega panel, so the pointer never "leaves"
  // the menu while moving down (or diagonally) into the panel
  _setHoverBridge() {
    const summary = this.querySelector('summary');
    if (!summary) return;
    const summaryRect = summary.getBoundingClientRect();
    const gap = Math.max(
      0,
      Math.round(this.content.getBoundingClientRect().top - summaryRect.bottom),
    );
    summary.style.setProperty('--hover-bridge-height', `${gap + 2}px`);
    summary.style.setProperty('--hover-bridge-left', `${-Math.round(summaryRect.left)}px`);
    summary.style.setProperty(
      '--hover-bridge-width',
      `${document.documentElement.clientWidth}px`,
    );
  }

  close() {
    if (this.classList.contains('mega') && this.mainDetailsToggle.hasAttribute('open'))
      HeaderDetailsDisclosure._lastMegaClose = performance.now();

    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle
      .querySelector('summary')
      .setAttribute('aria-expanded', false);

    this.content.classList.remove('is-left-aligned');

    if (this.hasAttribute('adjust-mega-menu-height'))
      this._removeContentHeight();
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();
    if (!this.contentAnimations) {
      this.contentAnimations = Array.from(
        this.querySelectorAll('.has-animation'),
      ).reduce((animations, element) => {
        const animation = element.getAnimations();
        return [...animations, ...animation];
      }, []);
    }

    if (this.mainDetailsToggle.hasAttribute('open')) {
      setTimeout(() => {
        this._enableSummaryLink();
      }, 50);
      // dropdown animation reset (skipped when switching between mega menus: that panel shows
      // instantly, and replaying an old cached fade made it flash or stay hidden depending on
      // which menu had been opened first)
      if (this.content.classList.contains('is-switching'))
        this.animations.forEach((animation) => animation.cancel());
      else this.animations.forEach((animation) => animation.play());
      // mega items animation reset
      this.contentAnimations.forEach((animation) => animation.play());

      document.body.setAttribute('header-menu-open', '');
      if (this.classList.contains('mega')) {
        document.body.setAttribute('header-mega-menu-open', '');
      }
    } else {
      this._disableSummaryLink();
      // dropdown animation cancel
      this.animations.forEach((animation) => animation.cancel());
      // mega items animation cancel
      this.contentAnimations.forEach((animation) => animation.cancel());

      document.body.removeAttribute('header-menu-open', '');
      if (this.classList.contains('mega')) {
        document.body.removeAttribute('header-mega-menu-open', '');
      }
    }
  }

  mouseEnterListener() {
    if (
      !document
        .querySelector('body')
        .hasAttribute('header-details-disclosure-edit')
    )
      this.open();
  }

  mouseLeaveListener() {
    if (
      !document
        .querySelector('body')
        .hasAttribute('header-details-disclosure-edit')
    )
      this.close();
  }

  disableListeners() {
    this.mainDetailsToggle.removeEventListener(
      'mouseenter',
      this._boundMouseEnterListener,
    );
    this.mainDetailsToggle.removeEventListener(
      'mouseleave',
      this._boundMouseLeaveListener,
    );
  }

  _setContentHeight(padding = 0) {
    const headerSection = document.querySelector('.js-header');
    const headerBottomBoundary =
      headerSection && headerSection.getBoundingClientRect().bottom > 0
        ? headerSection.getBoundingClientRect().bottom
        : 0;
    const combinedPanelCutoff = Math.round(headerBottomBoundary + padding);

    if (this.content.offsetHeight > window.innerHeight - combinedPanelCutoff) {
      this.content.style.setProperty(
        '--header-elements-height',
        `${combinedPanelCutoff}px`,
      );
      this.content.classList.add('has-height-control');
    }
  }

  _removeContentHeight() {
    this.content.classList.remove('has-height-control');
  }

  _summaryLinkListener(e) {
    e.preventDefault();
  }

  _enableSummaryLink() {
    if (this._summaryLink && !this.hasAttribute('summary-link-disabled'))
      this._summaryLink.removeEventListener('click', this._summaryLinkListener);
  }

  _disableSummaryLink() {
    if (this._summaryLink)
      this._summaryLink.addEventListener('click', this._summaryLinkListener);
  }
}

customElements.define('header-details-disclosure', HeaderDetailsDisclosure);
